import { Button, ClickEvent, Table, TableSelectionEvent, Toast } from "@mcleod/components";
import { AutogenLayoutMiscInvoiceProcessingToolPanel } from "./autogen/AutogenLayoutMiscInvoiceProcessingToolPanel";
import { MiscInvoiceProcessingLayout, MiscInvoiceProcessingTableType } from "./MiscInvoiceProcessing";
import { CommonDialogs } from "@mcleod/common";
import { DateUtil, LogManager, ModelRow, StringUtil } from "@mcleod/core";

const log = LogManager.getLogger("MiscInvoiceProcessingToolPanel");

export class MiscInvoiceProcessingToolPanel extends AutogenLayoutMiscInvoiceProcessingToolPanel {
    public type: MiscInvoiceProcessingTableType;
    private tableMiscInvoice: Table;
    private _searchCallback: () => void;

    override onLoad(): void {
        this.addMountListener(() => {
            this.initialize(this.owner);
        });
    }

    get searchCallback(): () => void {
        return this._searchCallback;
    }

    set searchCallback(searchCallback: () => void) {
        this._searchCallback = searchCallback;
    }

    private getAllVisibleButtons(): Button[] {
        return this.panelButtons?.findComponentsByType(Button)?.filter(button => button.visible);
    }

    initialize(layout: MiscInvoiceProcessingLayout) {
        this.type = layout.type;
        this.tableMiscInvoice = layout.tableMiscInvoice;
        this.syncButtonVisibility();
        if (this.getAllVisibleButtons()?.length > 0) {
            this.tableMiscInvoice?.addSelectionListener((event: TableSelectionEvent) => {
                this.tableOnSelect(event);
            });
        } else {
            this.removeAll();
        }
    }

    tableOnSelect(event: TableSelectionEvent) {
        this.syncToolPanel(event);
    }

    syncButtonVisibility() {
        this.buttonMarkReadyToProcess.visible = this.type === MiscInvoiceProcessingTableType.PENDING_REVIEW;
        this.buttonSendBackToReview.visible = this.type === MiscInvoiceProcessingTableType.READY_TO_PROCESS;
    }

    syncToolPanel(event: TableSelectionEvent) {
        this.buttonMarkReadyToProcess.enabled = event.newRows?.length > 0;
        this.buttonSendBackToReview.enabled = event.newRows?.length > 0;
    }

    async buttonMarkReadyToProcessOnClick(event: ClickEvent) {
        await this.buttonUpdateReadyToProcessOnClick(event, true);
    }

    async buttonSendBackToReviewOnClick(event: ClickEvent) {
        await this.buttonUpdateReadyToProcessOnClick(event, false);
    }

    async buttonUpdateReadyToProcessOnClick(event: ClickEvent, readyToProcess: boolean) {
        const selectedRows = this.tableMiscInvoice?.selectedRows || [];
        const modelRows = selectedRows.map(row => row.data).filter(row => row != null) as ModelRow[];

        if (modelRows.length > 0) {
            const updateRecords = await CommonDialogs.showYesNo(this.getUpdateReadyToProcessMessage(readyToProcess), "Update Record(s)?");

            if (updateRecords) {
                const button = event.target as Button;
                button.busy = true;
                try {
                    await this.updateReadyToProcess(modelRows, readyToProcess);
                } finally {
                    if (this.searchCallback) {
                        this.searchCallback();
                    }
                    button.busy = false;
                }
            }
        }
    }

    async updateReadyToProcess(modelRows: ModelRow[], readyToProcess: boolean) {
        let successCount = 0;
        let failureCount = 0;
        const errorMessages: string[] = [];
        await Promise.allSettled(modelRows.map(async row => {
            try {
                row.set("ready_to_process", readyToProcess);
                await row.post();
                successCount++;
                return { success: true };
            } catch (error) {
                const customerName = row.getString("customer.name", "--");
                const billDate = row.getDate("bill_date", null);
                const billDateString = billDate ? DateUtil.formatDate(billDate, "MM/dd/yyyy") : "--";
                log.error(`Failed to update 'ready to process status' for invoice with Customer [${customerName}], Invoice Date [${billDateString}]`, error);
                row.resetOriginalData();
                failureCount++;
                errorMessages.push(`Customer [${customerName}] Invoice Date [${billDateString}] - ${error?.message || error || "Unknown error"}`);
                return { success: false, error: error };
            }
        }));

        this.displayUpdateResult(successCount, failureCount, errorMessages);
    }

    private getUpdateReadyToProcessMessage(readyToProcess: boolean): string {
        return readyToProcess ? "Mark selected invoices as Ready to Process?" : "Send selected invoices back to Pending Review?";
    }

    private displayUpdateResult(successCount: number, failureCount: number, errorMessages?: string[]) {
        if (failureCount > 0 && errorMessages && errorMessages.length > 0) {
            const summary = (successCount > 0)
                ? `${StringUtil.pluralString(successCount, "Invoice", "Invoices")} updated successfully.\n${StringUtil.pluralString(failureCount, "Invoice", "Invoices")} failed to update.`
                : `${StringUtil.pluralString(failureCount, "Invoice", "Invoices")} failed to update.`;
            CommonDialogs.showError(summary + "\n\nDetails:\n" + errorMessages.join("\n"), "Update Errors");
        } else if (successCount > 0) {
            Toast.showToast(StringUtil.pluralString(successCount, "Invoice", "Invoices") + " updated successfully.");
        }
    }
}
